/**
 * SessionTelemetry — the domain service that makes forced logouts countable.
 *
 * Owns the two pieces of bookkeeping behind the `session_ended` event: a
 * persisted "this client had a session" marker (so first-time visitors opening
 * an authenticated URL don't count as forced logouts) and a `Ref` that
 * de-duplicates reports within one service lifetime (the route guard and the
 * session-absence effect can both fire for a single underlying event).
 *
 * Depends only on the platform-neutral `Analytics` tag, so it reports from any
 * platform whose runtime provides a layer for it — web today, mobile once the
 * `posthog-react-native` layer lands. Platform wiring (Sentry breadcrumbs, the
 * runtime) stays in the app facades: `apps/start/src/services/session-end.ts`.
 */
import { Context, Effect, Layer, Ref } from 'effect';
import { getAtomKvsStorage, type AtomKvsStorage } from '../atoms/kvs';
import { Analytics } from './analytics-tag';
import {
  isForcedLogoutCause,
  shouldReportForcedLogout,
  type SessionEndCause,
} from './session-end';

/**
 * Evidence that this client had a session. Deliberately a bare flag, not user
 * data — it outlives the session it describes.
 */
const KNOWN_SESSION_KEY = 'listening.had_session';

export type SessionEndInput = {
  cause: SessionEndCause;
  /** HTTP status of the failed session check, when there was one. */
  errorStatus?: number | null;
};

export const makeSessionTelemetry = (options?: {
  /**
   * Test seam. Defaults to the kvs boot-slot storage (web localStorage,
   * mobile MMKV), read lazily so boot-time `setAtomKvsStorage` is honored.
   */
  storage?: AtomKvsStorage;
}) =>
  Effect.gen(function* () {
    const analytics = yield* Analytics;
    // De-dupes reports for one teardown. Its lifetime is the layer's lifetime
    // — on web the facade builds one runtime per page load.
    const reported = yield* Ref.make(false);

    const storage = () => options?.storage ?? getAtomKvsStorage();
    // Storage access fails open (Safari private mode, storage-blocked
    // iframes) — telemetry must never crash the app. Same policy as
    // `atoms/kvs.ts`.
    const hadKnownSession = () => {
      try {
        return storage().getItem(KNOWN_SESSION_KEY) === '1';
      } catch {
        return false;
      }
    };
    const writeMarker = () => {
      try {
        storage().setItem(KNOWN_SESSION_KEY, '1');
      } catch {
        // fails open — see file header comment
      }
    };
    const clearMarker = () => {
      try {
        storage().removeItem(KNOWN_SESSION_KEY);
      } catch {
        // fails open — see file header comment
      }
    };

    return {
      /** Call whenever an authenticated user is observed. */
      rememberActiveSession: Effect.gen(function* () {
        yield* Ref.set(reported, false);
        writeMarker();
      }),

      /**
       * Record that the session ended. Only forced logouts are reported as
       * events, at most once per service lifetime, and only for a client that
       * actually had a session.
       */
      markSessionEnd: ({ cause, errorStatus = null }: SessionEndInput) =>
        Effect.gen(function* () {
          const report = shouldReportForcedLogout({
            alreadyReported: yield* Ref.get(reported),
            cause,
            hadKnownSession: hadKnownSession(),
          });
          if (report) {
            yield* Ref.set(reported, true);
            yield* analytics.capture('session_ended', {
              cause,
              error_status: errorStatus,
              forced: isForcedLogoutCause(cause),
            });
          }
          // Cleared for every ending, forced or not. Leaving it set would mark
          // the client permanently, so each later visit to an authenticated
          // URL while signed out would look like a fresh forced logout — the
          // exact noise the marker exists to prevent. Re-set by
          // `rememberActiveSession` on the next successful guard.
          //
          // Known limitation: the marker is shared across tabs but `reported`
          // is per runtime (per tab), so a forced logout in one tab can
          // suppress reporting in another still-open tab until that tab's
          // next successful guard. Under-counting a rare case beats
          // permanently over-counting the common one.
          clearMarker();
        }),
    };
  });

export class SessionTelemetry extends Context.Service<SessionTelemetry>()(
  '@listening/frontend/analytics/SessionTelemetry',
  { make: makeSessionTelemetry },
) {
  // v3's `Effect.Service` auto-generated a `.Default` layer (callable, since
  // `makeSessionTelemetry` takes optional args); v4 no longer does, so the
  // layer is built explicitly here — `this.make()` with no args reproduces
  // the same default (boot-slot kvs storage). Consumers that need a custom
  // `storage` (tests) call `makeSessionTelemetry` directly instead of going
  // through this layer.
  static readonly layer = Layer.effect(this, this.make());
}
